import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "@/lib/site";

const Logo = ({ className = "h-11 w-auto sm:h-12" }) => (
  <img
    src="/images/riverstone-logo.webp"
    alt="Riverstone Partners"
    width="2000"
    height="668"
    className={className}
    data-testid="nav-logo"
  />
);

const DesktopDropdown = ({ item }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="nav-link-solutions"
        onClick={() => setOpen((v) => !v)}
        className={`flex min-h-[44px] items-center gap-1 text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-200 hover:text-forest ${
          pathname.startsWith("/solutions") ? "text-forest" : "text-charcoal/80"
        }`}
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 top-full z-50 w-80 -translate-x-1/2 pt-3"
            data-testid="nav-solutions-dropdown"
          >
            <div className="border border-charcoal/10 bg-white py-3 shadow-lift">
              {item.children.map((s) => (
                <Link
                  key={s.slug}
                  to={`/solutions/${s.slug}`}
                  className="group flex min-h-[44px] items-center justify-between px-5 py-2.5 text-sm text-charcoal/80 transition-colors hover:bg-sage/60 hover:text-forest"
                >
                  {s.name}
                  <ArrowRight className="h-3.5 w-3.5 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                </Link>
              ))}
              <div className="mt-2 border-t border-charcoal/10 pt-2">
                <Link
                  to="/solutions"
                  className="flex min-h-[44px] items-center px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-river hover:text-forest"
                >
                  All Solutions
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setMobileOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const simpleLinks = NAV_LINKS.filter((l) => !l.children);

  return (
    <>
      <header
      className={`sticky top-0 z-50 border-b border-charcoal/10 bg-ivory/90 backdrop-blur-md transition-shadow duration-300 ${
        scrolled ? "shadow-lift" : ""
      }`}
      data-testid="site-header"
    >
      <div className="shell flex h-[76px] items-center justify-between gap-6">
        <Link to="/" aria-label="Riverstone Partners — Home" className="shrink-0">
          <Logo className="h-10 w-auto sm:h-11 lg:h-12" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((item) =>
            item.children ? (
              <DesktopDropdown key={item.label} item={item} />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `min-h-[44px] text-[13px] font-medium uppercase leading-[44px] tracking-[0.14em] transition-colors duration-200 hover:text-forest ${
                    isActive ? "text-forest" : "text-charcoal/80"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
          <Link
            to="/contact"
            data-testid="nav-lets-talk-button"
            className="btn-primary min-h-[44px] px-6 py-2 text-[12px]"
          >
            Let’s Talk
          </Link>
        </nav>

        <button
          type="button"
          className="flex h-11 w-11 items-center justify-center text-navy lg:hidden"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          data-testid="nav-mobile-toggle"
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
    </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 top-[76px] z-40 overflow-y-auto bg-navy px-6 pb-16 pt-8 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav aria-label="Mobile" className="flex flex-col gap-1">
              {NAV_LINKS.map((item) => (
                <div key={item.label}>
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block min-h-[52px] border-b border-ivory/10 py-4 font-serif text-2xl text-ivory"
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="flex flex-col py-2">
                      {item.children.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/solutions/${s.slug}`}
                          className="flex min-h-[44px] items-center gap-2 py-2 pl-4 text-sm text-ivory/70"
                        >
                          <span className="h-px w-4 bg-river" aria-hidden="true" />
                          {s.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-lets-talk-button"
                className="mt-6 flex min-h-[52px] items-center justify-center bg-forest px-6 text-sm font-semibold uppercase tracking-[0.14em] text-ivory"
              >
                Let’s Talk
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
